import { Component, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { FiDataService } from '../../core/services/fi-data.service';
import { Subscription } from 'rxjs';
import { take } from 'rxjs/operators';
import { OverlayModule, Overlay, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { ConfirmationModalComponent } from '../../shared/components/confirmation-modal/confirmation-modal';

@Component({
  selector: 'app-request-consent',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, OverlayModule],
  templateUrl: './request-consent.html',
  styleUrls: ['./request-consent.scss'],
})
export class RequestConsent implements OnInit, OnDestroy { // Added OnDestroy for cleanup
  consentForm!: FormGroup;
  isLoading = false;
  errorMessage = '';
  successMessage = '';
  consentResponse: any = null; // TODO: Add proper type for consentResponse

  showConsentsList = false;
  consents: any[] = [];
  private subscriptions: Subscription = new Subscription();
  private confirmationOverlayRef: OverlayRef | null = null;

  constructor(
    private fb: FormBuilder,
    private fiDataService: FiDataService,
    private router: Router,
    private cdr: ChangeDetectorRef,
    private overlay: Overlay
  ) {
    this.initializeForm();
  }

  ngOnInit(): void {
    //Apply gradient background to body
    document.body.classList.add('consent-page-active');
    // Check for existing active session
    const activeConsentId = localStorage.getItem('active_consent_id');
    if (activeConsentId) {
      this.router.navigate(['/dashboard']);
      return;
    }
  }

  private initializeForm(): void {
    this.consentForm = this.fb.group({
      partyIdentifierValue: ['', [Validators.required, Validators.pattern(/^[0-9]{10}$/)]],
    });
  }

  onSubmit(): void {
    if (this.consentForm.invalid) {
      this.markFormGroupTouched(this.consentForm);
      return;
    }

    this.isLoading = true;
    this.errorMessage = '';
    const mobile = this.consentForm.value.partyIdentifierValue;

    // First fetch existing consents
    const fetchSub = this.fiDataService.getConsentsList({
      mobileNumber: mobile,
      productID: 'TESTBANXX'
    }).subscribe({
      next: (response) => {
        this.isLoading = false;
        if (response.status === 'success' && response.data && response.data.length > 0) {
          this.consents = response.data.filter((c: any) => c.status === 'ACTIVE' || c.status === 'APPROVED');
          this.showConsentsList = true;
        } else {
          this.consents = [];
          this.showConsentsList = true;
        }
        this.cdr.detectChanges();
      },
      error: (err) => {
        this.isLoading = false;
        this.errorMessage = 'Failed to fetch existing consents. You can try creating a new one.';
        this.showConsentsList = true;
        this.consents = [];
        this.cdr.detectChanges();
      }
    });

    this.subscriptions.add(fetchSub);
  }

  selectExistingConsent(consent: any): void {
    const consentId = consent.consentID || consent.id;
    if (consentId) {
      localStorage.setItem('active_consent_id', consentId);
      localStorage.setItem('consent_mobile_number', this.consentForm.value.partyIdentifierValue);
      localStorage.setItem('consent_product_id', consent.productID);
      localStorage.setItem('consent_account_id', consent.accountID);
      localStorage.setItem('active_consent_handle', consent.consentHandle);
      this.router.navigate(['/dashboard']);
    }
  }


  revokeConsent(event: Event, consent: any): void {
    event.stopPropagation(); // Prevent navigating to dashboard
    const consentId = consent.consentID || consent.id;
    if (!consentId) return;

    this.openConfirmationModal({
      title: 'Revoke Consent?',
      message: 'Are you sure you want to revoke this consent? This action cannot be undone.',
      icon: 'assets/icons/img_trash.svg',
      closeLabel: 'Cancel',
      confirmLabel: 'Revoke',
      confirmColor: 'danger',
      onConfirm: () => {
        this.executeRevokeConsent(consentId);
      }
    });
  }

  private executeRevokeConsent(consentId: string): void {
    this.isLoading = true;
    this.fiDataService.revokeConsent(consentId).pipe(take(1)).subscribe({
      next: (response) => {
        if (response.status === 'success') {
          // Remove from local list
          this.consents = this.consents.filter(c => (c.consentID || c.id) !== consentId);
          this.successMessage = 'Consent revoked successfully.';
          setTimeout(() => this.successMessage = '', 3000);
        } else {
          this.errorMessage = response.message || 'Failed to revoke consent.';
        }
        this.isLoading = false;
        this.cdr.detectChanges();
      },
      error: (err) => {
        this.errorMessage = 'An error occurred while revoking consent.';
        this.isLoading = false;
        this.cdr.detectChanges();
      }
    });
  }

  createNewConsent(): void {
    this.isLoading = true;
    this.errorMessage = '';
    this.successMessage = '';
    this.consentResponse = null;

    const mobile = this.consentForm.value.partyIdentifierValue;
    // const baseHref = document.querySelector('base')?.getAttribute('href') || '/';
    // const redirectUrl = `${window.location.origin}${baseHref}${this.router.url.replace('/request-consent', '/consent-callback')}`;
    // const redirectUrl = window.location.origin + '/consent-callback';
    const redirectUrl = window.location.origin + '/banxxv2/consent-callback';

    const requestData: any = {
      productID: 'TESTBANXX',
      vua: `${mobile}@onemoney`,
      partyIdentifierType: 'MOBILE',
      partyIdentifierValue: mobile,
      accountID: mobile,
      redirectUrl: redirectUrl
    };

    localStorage.setItem('consent_mobile_number', requestData.partyIdentifierValue);
    localStorage.setItem('consent_product_id', requestData.productID);
    localStorage.setItem('consent_account_id', requestData.accountID);

    const sub = this.fiDataService.requestConsent(requestData).pipe(take(1)).subscribe({
      next: (response) => {
        this.isLoading = false;
        if (response.status === 'success' || (response.data && response.data.webRedirectionUrl)) {
          this.successMessage = 'Consent request submitted successfully!';
          this.consentResponse = response;
          if (response.data && response.data.consent_handle) {
            localStorage.setItem('active_consent_handle', response.data.consent_handle);
            this.openConsentUrl();
          }
        } else {
          // Handle application-level errors (like NoSuchVersion) that come in a 200 response
          this.errorMessage = response.message || response.errorCode || 'Request failed. Please try again.';
          console.error('Consent Request Failed:', response);
        }
        this.cdr.detectChanges();
      },
      error: (error) => {
        this.isLoading = false;
        // Handle HTTP errors (4xx, 5xx)
        const errorBody = error.error;
        this.errorMessage = errorBody?.message || errorBody?.errorCode || error.message || 'An error occurred. Please try again.';
        console.error('Consent Request Error:', error);
        this.cdr.detectChanges();
      }
    });
    this.subscriptions.add(sub);
  }

  openConsentUrl(): void {
    if (this.consentResponse?.data?.webRedirectionUrl) {
      // Redirect in the same window for a seamless flow (no change)
      window.location.href = this.consentResponse.data.webRedirectionUrl;
    }
  }

  // Removed unused showConsentInIframe method

  private markFormGroupTouched(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach(key => {
      const control = formGroup.get(key);
      control?.markAsTouched();

      if (control instanceof FormGroup) {
        this.markFormGroupTouched(control);
      }
    });
  }

  // Helper method to reset form (no change, but called if needed)
  resetForm(): void {
    this.consentForm.reset({
      productID: 'TESTBANXX',
      partyIdentifierType: 'MOBILE'
    });
    this.errorMessage = '';
    this.successMessage = '';
    this.consentResponse = null;
  }

  ngOnDestroy(): void {
    // Remove gradient background from body
    document.body.classList.remove('consent-page-active');

    this.subscriptions.unsubscribe();
    if (this.confirmationOverlayRef) {
      this.confirmationOverlayRef.dispose();
    }
  }

  private openConfirmationModal(config: {
    title: string;
    message: string;
    icon?: string;
    closeLabel?: string;
    confirmLabel: string;
    confirmColor: 'danger' | 'primary';
    onConfirm: () => void;
  }): void {
    if (this.confirmationOverlayRef) {
      this.confirmationOverlayRef.dispose();
    }

    this.confirmationOverlayRef = this.overlay.create({
      hasBackdrop: true,
      backdropClass: 'cdk-overlay-dark-backdrop',
      positionStrategy: this.overlay.position().global().centerHorizontally().centerVertically(),
      scrollStrategy: this.overlay.scrollStrategies.block(),
    });

    const portal = new ComponentPortal(ConfirmationModalComponent);
    const componentRef = this.confirmationOverlayRef.attach(portal);

    componentRef.instance.title = config.title;
    componentRef.instance.message = config.message;
    if (config.icon) componentRef.instance.icon = config.icon;
    if (config.closeLabel) componentRef.instance.closeLabel = config.closeLabel;
    componentRef.instance.confirmLabel = config.confirmLabel;
    componentRef.instance.confirmColor = config.confirmColor;

    componentRef.instance.confirm.subscribe(() => {
      config.onConfirm();
      this.closeConfirmationModal();
    });

    componentRef.instance.cancel.subscribe(() => {
      this.closeConfirmationModal();
    });

    this.confirmationOverlayRef.backdropClick().subscribe(() => this.closeConfirmationModal());
    this.cdr.detectChanges();
  }

  private closeConfirmationModal(): void {
    if (this.confirmationOverlayRef) {
      this.confirmationOverlayRef.dispose();
      this.confirmationOverlayRef = null;
    }
    this.cdr.detectChanges();
  }
}